// Lists the reservations that carry extra items (lisätuotteet), and hides the fact that
// nothing about them is queryable.
//
// Extras live on the reservation as `items: [{ item, amount }]`, written in exactly one place -
// falcon-a/functions/reservations.js:3453, the enduser `createmultiv3` path. There is no index
// of them, and reservations cannot be range-queried by date at all, because the start time sits
// at resourcegroups[0].start, inside an array.
//
// So the report walks the sharded day index the court calendars read,
// bookings/{cid}/{YYYYMMDD}/{shard} - which does not carry `items` either. _minifyReservation
// (reservations-utils.js:368) keeps only a whitelist and `items` is not on it. The one trace
// left is the note the booking path writes per extra item, tagged `u: 'system@falcon.app'`
// (reservations.js:3459). That tag appears nowhere else in the backend, so it is the marker
// meaning "open this reservation's document". Everything else a row needs - date, time, payer,
// state - is already in the index entry, which is what lets a row survive a failed doc read.
//
// Two consequences worth knowing:
//
//   - An admin who edits or deletes that auto-generated note in ReservationModal destroys the
//     marker, and the reservation silently drops out of this report while keeping its items.
//     Inherent to the approach. The real fix is to whitelist `items` in the indexer.
//   - Recurring enduser bookings never appear: reservations-createrecurrent does not even
//     destructure `items` (reservations.js:5053) and passes null in its place (:5161), so the
//     extras are dropped before anything is stored.

import moment from 'moment'

// The tag on the notes the booking path generates for extras. Owned by the backend; if it ever
// changes, this line is the only edit here.
export const SYSTEM_NOTE_AUTHOR = 'system@falcon.app'

// Same set the backend's getBusytimesOfDay skips, and what the court calendar means by a
// booking that did not happen (CourtsContainer.jsx:445).
export const EXCLUDED_STATES = ['cancelled', 'onsale', 'moved']

// Two ceilings, because the two phases cost differently.
//
// The range is one collection read per day. The dates are typed by hand - the picker's presets
// only render for busyvisual - so a stray year is easy.
export const MAX_RANGE_DAYS = 31

// The candidates are one document read each, and document reads are billed. The candidate count
// is known before a single read is issued, so this ceiling is free to enforce: past it the
// report refuses and says what it found, rather than spending thousands of reads on a wait
// nobody would sit through. A month of extras bookings is normally nowhere near this.
//
// Deliberately a refusal and not a truncation: 200 rows of a real 1500 would export to CSV
// looking exactly like a complete report.
export const MAX_RESERVATION_READS = 200

export const exceedsReadLimit = (count) => count > MAX_RESERVATION_READS

export const dateRangeKeys = (startdate, enddate) => {
  const start = moment.utc(startdate, 'YYYY-MM-DD', true)
  const end = moment.utc(enddate, 'YYYY-MM-DD', true)
  if (!start.isValid() || !end.isValid()) return { days: [], error: 'invalid-range' }
  if (end.isBefore(start)) return { days: [], error: 'reversed-range' }
  const length = end.diff(start, 'days') + 1
  if (length > MAX_RANGE_DAYS) return { days: [], error: 'range-too-long' }
  const days = []
  for (let i = 0; i < length; i++) {
    days.push(start.clone().add(i, 'day').format('YYYY-MM-DD'))
  }
  return { days, error: null }
}

const noteList = (entry) => {
  const notes = entry && entry.data ? entry.data.notes : null
  return Array.isArray(notes) ? notes : []
}

export const extrasNotes = (entry) => noteList(entry).filter((note) => note && note.u === SYSTEM_NOTE_AUTHOR)

export const hasExtrasMarker = (entry) => extrasNotes(entry).length > 0

// The fallback label when the marker is there but the document's items are not. Reads
// "Mailavuokra 2kpl" - the backend's own wording, not this file's.
export const extrasFromNotes = (entry) =>
  extrasNotes(entry)
    .map((note) => note.note)
    .filter((text) => text)
    .join(', ')

// An empty state means reserved, the same assumption Reservation.getState() makes.
const stateOf = (entry) => {
  const state = entry && entry.data ? entry.data.state : null
  return state || 'reserved'
}

export const isExcludedState = (entry) => EXCLUDED_STATES.includes(stateOf(entry))

export const candidatesFromEntries = (entries, dateKey) =>
  (entries || [])
    .filter((entry) => entry && entry.id && hasExtrasMarker(entry) && !isExcludedState(entry))
    .map((entry) => ({ id: entry.id, dateKey, entry }))

// Keyed on the reservation id alone, not id + date, and that is lossless rather than lucky:
// extras notes are written with all: false, and _transformReservations keeps an occurrence's
// notes only when `n.all || n.occurency === thatDate` (reservations-utils.js:274). At most one
// day in any range can carry the marker for a given reservation.
export const dedupeCandidates = (candidates) => {
  const byId = new Map()
  for (const candidate of candidates || []) {
    if (!byId.has(candidate.id)) byId.set(candidate.id, candidate)
  }
  return Array.from(byId.values())
}

// The booked quantity is `amount` on the wrapper. Do not reach for `line.item.amounts`: that is
// the quantity dropdown's options, e.g. [1,2,3,4], attached to the product at ui.js:520. Both
// the client's `delete _x.amounts` and the backend's `delete ii.amounts` (reservations.js:3455)
// operate on the wrapper instead, so it is still there in stored documents.
export const formatExtras = (items) =>
  (Array.isArray(items) ? items : [])
    .map((line) => {
      const name = (line && line.item && line.item.displayName) || 'Tuntematon tuote'
      const amount = Number(line && line.amount)
      return amount > 1 ? name + ' x' + amount : name
    })
    .join(', ')

export const extrasCount = (items) =>
  (Array.isArray(items) ? items : []).reduce((sum, line) => sum + (Number(line && line.amount) || 0), 0)

// _extractExtras (falcon-a/functions/ui.js:525) flattens price.g to a cents integer before a
// product is ever offered, and drops any product that has no price.g - so a stored item.price
// is always a number, and 0 is a legitimate free extra. The object branch below is kept only
// for a row written before that resolver existed and is not expected to fire.
export const itemPriceCents = (item) => {
  if (!item) return null
  if (typeof item.price === 'number') return item.price
  if (item.price && typeof item.price.g === 'number') return item.price.g
  return null
}

// Null rather than a partial sum: a total that quietly omits a line is worse than no total.
export const extrasTotalCents = (items) => {
  const list = Array.isArray(items) ? items : []
  if (list.length === 0) return null
  let total = 0
  for (const line of list) {
    const cents = itemPriceCents(line && line.item)
    if (cents === null) return null
    total += cents * (Number(line.amount) || 0)
  }
  return total
}

const personName = (person) =>
  person ? [person.firstname, person.lastname].filter((part) => part).join(' ').trim() : ''

// Same-named people exist, so the identifier column carries the email or the businessId and is
// never left blank when either is known.
//
// Deliberately not Reservation.getFirstPayerSimple(): it dereferences payers[0].contacts[0]
// unguarded (Classes/Reservation.js:37) and throws on an organisation that has no contacts.
export const payerCells = (persons) => {
  const payers = persons && Array.isArray(persons.payers) ? persons.payers : []
  const syncusers = persons && Array.isArray(persons.syncusers) ? persons.syncusers : []
  const payer = payers[0] || null
  // When an organisation pays, the human who booked is only recoverable from syncusers - the
  // 'Varaaja:' note that would have named them is clobbered by the extras notes
  // (reservations.js:3384 assigns it, :3456 overwrites the whole array).
  const booker = personName(syncusers[0])
  if (!payer) return { payer: '', identifier: '', booker }
  if (payer.businessId) return { payer: payer.name || '', identifier: payer.businessId, booker }
  return { payer: personName(payer), identifier: payer.email || '', booker }
}

// Court and category come from the reservation document, never from the index entry: every
// per-resource entry is written under the same map key, { [r.id]: minified }
// (reservations-utils.js:416), so the entry that survives keeps only whichever resource was
// written last.
export const resourceCells = (doc, entry, resourcesById) => {
  const group = doc && Array.isArray(doc.resourcegroups) ? doc.resourcegroups[0] : null
  const embedded = group && Array.isArray(group.resources) ? group.resources : []
  const ids = embedded.map((resource) => resource && resource.id).filter((id) => id)
  if (ids.length === 0 && entry && entry.resourceId) ids.push(entry.resourceId)
  const names = []
  const categories = []
  ids.forEach((id, index) => {
    // customerdata.resources is the authority for the current name and the category, but it is
    // filtered to state === 'active' (withAuthentication.js:57), so a retired court falls back
    // to the snapshot stored on the reservation, then to the bare id.
    const known = resourcesById ? resourcesById[id] : null
    const snapshot = embedded[index]
    names.push((known && known.displayName) || (snapshot && snapshot.displayName) || id)
    const category = (known && known.category) || (snapshot && snapshot.category)
    if (category && categories.indexOf(category) === -1) categories.push(category)
  })
  return { resource: names.join(', '), category: categories.join(', ') }
}

// Date, time and duration come from the index entry, not from the document. The entry is the
// actual occurrence, it cannot fall outside the range the user asked for, and it is the same
// pair of instants the calendar renders - so the report and the calendar agree on screen.
// Both are absolute, which is what makes a booking across midnight come out right.
export const buildRow = ({ candidate, doc, resourcesById }) => {
  const entry = candidate.entry
  const start = moment.utc(entry.start)
  const end = moment.utc(entry.end)
  const hasTimes = start.isValid() && end.isValid()
  const items = doc && Array.isArray(doc.items) ? doc.items : []
  const missingitems = items.length === 0
  const persons = (doc && doc.persons) || (entry.data && entry.data.persons) || null
  const { payer, identifier, booker } = payerCells(persons)
  const { resource, category } = resourceCells(doc, entry, resourcesById)
  return {
    id: candidate.id,
    date: candidate.dateKey,
    starttime: hasTimes ? start.format('HH:mm') : '',
    duration: hasTimes ? end.diff(start, 'minutes') : null,
    resource,
    category,
    payer,
    identifier,
    booker,
    extras: missingitems ? extrasFromNotes(entry) : formatExtras(items),
    extrascount: missingitems ? null : extrasCount(items),
    extrassum: missingitems ? null : extrasTotalCents(items),
    missingitems,
    loadfailed: !doc,
  }
}

export const sortRows = (rows) =>
  (rows || [])
    .slice()
    .sort((a, b) => (a.date + a.starttime).localeCompare(b.date + b.starttime) || a.id.localeCompare(b.id))
